import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, LogOut, Users, Crown, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { maxPlayersFor } from "@/lib/limits";
import { ThemeToggle } from "@/components/ThemeToggle";

const Account = () => {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const { workspaces, active, activeSubscription, setActive, loading: wsLoading } = useWorkspaces();

  useEffect(() => {
    if (!loading && !user) navigate("/auth", { replace: true });
  }, [user, loading, navigate]);

  if (loading || wsLoading || !user) {
    return <main className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</main>;
  }

  const tier = activeSubscription?.tier ?? "free";
  const cap = maxPlayersFor(tier);

  return (
    <main className="min-h-screen px-4 py-10">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-2xl gradient-fun flex items-center justify-center shadow-pop">
              <Brain className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-extrabold text-xl">SameBrain</span>
          </Link>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <Button variant="ghost" size="sm" onClick={async () => { await signOut(); navigate("/"); }}>
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </header>

        <Card className="p-6 border-2 shadow-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Signed in as</div>
              <div className="text-lg font-bold">{user.email}</div>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-2 shadow-card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold flex items-center gap-2"><Users className="h-5 w-5" /> Workspaces</h2>
          </div>
          <div className="space-y-2">
            {workspaces.map((w) => (
              <button
                key={w.id}
                onClick={() => setActive(w.id)}
                className={`w-full text-left rounded-xl border-2 p-4 transition-bounce ${
                  active?.id === w.id ? "border-primary bg-primary/5 shadow-soft" : "border-border hover:border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold">{w.name}</div>
                    <div className="text-xs text-muted-foreground">{w.slug}</div>
                  </div>
                  {w.owner_id === user.id && <Badge variant="secondary"><Crown className="h-3 w-3 mr-1" /> Owner</Badge>}
                </div>
              </button>
            ))}
          </div>
        </Card>

        {active && (
          <Card className="p-6 border-2 shadow-card space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2"><Sparkles className="h-5 w-5" /> Plan</h2>
              <Badge className="capitalize text-base px-3 py-1">{tier}</Badge>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="rounded-lg bg-muted p-3">
                <div className="text-muted-foreground text-xs">Max players per room</div>
                <div className="text-2xl font-extrabold">{cap}</div>
              </div>
              <div className="rounded-lg bg-muted p-3">
                <div className="text-muted-foreground text-xs">Seats</div>
                <div className="text-2xl font-extrabold">{activeSubscription?.seats ?? 1}</div>
              </div>
            </div>
            {tier === "free" && (
              <Button onClick={() => navigate("/pricing")} className="w-full h-12 gradient-primary font-bold">
                Upgrade to Pro
              </Button>
            )}
          </Card>
        )}
      </div>
    </main>
  );
};

export default Account;
